import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { API_ENDPOINTS } from "../config/api";

const Dashboard: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);

  const COLORS = ["#131d50", "#8727d6", "#ff7f0e", "#1f77b4"];
  const STATUS_COLOR_MAP: Record<string, string> = {
  "NC": "#ff4f58",            // NC
  "CE": "#001d3d",    // CE
  "CEN": "#802dc8",     // CEN
};

  const fetchDashboard = async () => {
    const username = localStorage.getItem("username");
    const password = localStorage.getItem("password");

    if (!username || !password) {
      setData([{ Error: "Missing login credentials" }]);
      return;
    }

    try {
      const res = await fetch(API_ENDPOINTS.dashboard, {
        method: "POST",  // ✅ must be POST if sending a body
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),  // ✅ works with verify_auth
      });

      const json = await res.json();
      console.log("📊 Filtered dashboard data:", json);

      if (!Array.isArray(json)) {
        setData([{ Error: "Unexpected data format from backend" }]);
        return;
      }

      setData(json);

      const summaryRow = json[0];
      if (!summaryRow) return;

      const statusMap: any = {
        "CE (Contacto Efectivo)": "CE",
        "NC (No Contesta)": "NC",
        "CEN (Contacto Efectivo Negativo)": "CEN",
      };

      const counts = Object.entries(summaryRow)
        .filter(([key]) => key !== "Total Called Customers")
        .map(([key, value]) => ({
          status: statusMap[key] || key,
          count: Number(value),
        }));

      setChartData(counts);

    } catch (err) {
      console.error("❌ Fetch error:", err);
      setData([{ Error: "Failed to fetch dashboard data" }]);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const downloadCSV = () => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((key) => JSON.stringify(row[key] || "")).join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "dashboard.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-2xl font-bold mb-4">Survey Dashboard</h2>

      <div className="flex justify-between items-center mb-2">
        <h3 className="text-lg font-semibold">📋 Call Summary Table</h3>
        <button
          onClick={downloadCSV}
          className="bg-[#001d3d] text-white px-3 py-1 rounded text-sm hover:opacity-90"
        >
          Download CSV
        </button>
      </div>


      <div className="overflow-x-auto mb-6">
        <table className="table-auto w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              {data.length > 0 &&
                Object.keys(data[0]).map((k) => (
                  <th key={k} className="border px-3 py-1 text-left font-semibold">
                    {k}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row: any, i) => (
              <tr key={i}>
                {Object.values(row).map((val, j) => (
                  <td key={j} className="border px-3 py-1">
                    {String(val)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 border rounded p-4">
          <h3 className="text-lg font-semibold mb-2"> Call Outcome - Bar Chart</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="status"
                tick={{ fontSize: 12 }} // 👈 X-axis font size
              />
              <YAxis
                tick={{ fontSize: 12 }} // 👈 Y-axis font size to match X
              />
              <Tooltip />
              <Bar dataKey="count">
                {chartData.map((entry, index) => (
                  <Cell
                    key={`bar-${index}`}
                    fill={STATUS_COLOR_MAP[entry.status] || "#999"}
                  />
                ))}
              </Bar>
              {/* Legend removed */}
            </BarChart>
          </ResponsiveContainer>



        </div>

        <div className="bg-gray-50 border rounded p-4">
          <h3 className="text-lg font-semibold mb-2"> Call Outcome - Pie Chart</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}  // 👈 Show ratio
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={STATUS_COLOR_MAP[entry.status] || "#999"}
                  />
                ))}
              </Pie>
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Tooltip formatter={(value, name) => [`${value} calls`, name]} />
            </PieChart>
          </ResponsiveContainer>


        </div>
      </div>
    </div>
  );
};

export default Dashboard;


